import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useProcessDraftFieldEditing } from './processes/useProcessDraftFieldEditing';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, FileSearch, Loader2, RotateCcw, Search, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import axiosInstance from '../api/axios';
import { ProcessDraftReviewTable, formatSoxCycleDisplay } from './processes/ProcessDraftReviewTable.tsx';
import { AppSelect } from '../shared/common/AppSelect.tsx';
import {
  defaultSelectedProcessDraftIds,
  draftRowHasAnyMergedError,
  selectedDraftRowsHaveMergedErrors,
} from './processes/processDraftValidation.ts';
import { BulkDocumentUploadZone } from './bulk/BulkDocumentUploadZone.tsx';
import { BulkExtractionProgressOverlay } from './bulk/BulkExtractionProgressOverlay.tsx';
import { Tooltip, TooltipProvider } from '../admin/components/shared/Tooltip.tsx';
import { createProcessSchema, PROCESS_SOURCE } from './processes/schema';
import { API_ENDPOINTS } from '../api/endpoints';
import AISOXProcessDesigner from './AISOXProcessDesigner';
import ProcessEditForm from './processes/ProcessEditForm';
import ProcessGenerateFlowchartPanel from './processes/ProcessGenerateFlowchartPanel';
import { PROCESS_MANUAL_CREATE_FILE_CAP } from './processes/constants';
import { uploadFilesAndComplete } from '../utils/fileUpload';
import { MODULE_UPLOAD } from '../utils/moduleUpload.ts';
import { getApiErrorMessage, resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import { useCreateProcessMutation } from '../queries/processes/useProcessMutations';
import { SOX_CYCLES_QUERY_KEY, cycleProcessesQueryKey, processesQueryKey } from '../queryKeys.ts';
import { mapProcessValidationErrorsByFileDataIndex } from './processes/validations.js';
import { clearProcessDraftCache, extractProcessFileIds } from './processes/functional';
import { soxProcessesListPath } from '../routing/processNavigation.ts';
import { useProcessBackNavigation } from '../hooks/useProcessBackNavigation.ts';
import { mapAiExtractedToDraftProcesses } from './processes/processDraftMappers';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';
import ReadOnlyBanner from './ReadOnlyBanner.tsx';

/** Align bulk/AI draft toolbar search + filter controls (matches `AppSelect` `size="control"`). */
const DRAFT_TOOLBAR_SEARCH_CLASS =
  'h-10 w-full rounded-lg border border-gray-200 bg-white py-0 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500';

const UPLOAD_EXTENSIONS = ['xlsx', 'csv'];

const PROCESS_BULK_HEADER_VALIDATION_ERROR_MESSAGE =
  'Header validation failed. Please verify the file headers match the sample document and try again.';

const PROCESS_BULK_ACCEPT = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/csv': ['.csv']
};

/** Match spreadsheet / API headers regardless of casing or separators (e.g. `Description` vs `description`). */
const normalizeHeaderKey = (key) =>
  String(key || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

const getFirstValue = (row, keys) => {
  if (!row || typeof row !== 'object') return '';
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null) {
      return row[key];
    }
  }
  const entries = Object.entries(row);
  for (const key of keys) {
    const want = normalizeHeaderKey(key);
    const hit = entries.find(([k]) => normalizeHeaderKey(k) === want);
    if (hit && hit[1] !== undefined && hit[1] !== null) {
      return hit[1];
    }
  }
  return '';
};

const toStringValue = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  return String(value).trim();
};

const toStringArray = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => toStringValue(item)).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const inferProcessName = (row) => {
  const direct = toStringValue(
    getFirstValue(row, ['process_name', 'name', 'processName', 'process', 'process_title', 'processTitle'])
  );
  if (direct) return direct;
  const entries = Object.entries(row || {});
  const dynamicKeyMatch = entries.find(([key, value]) => {
    const normalizedKey = String(key || '').toLowerCase().replace(/[_\s-]/g, '');
    if (!normalizedKey.includes('process') || !normalizedKey.includes('name')) return false;
    return Boolean(toStringValue(value));
  });
  return dynamicKeyMatch ? toStringValue(dynamicKeyMatch[1]) : '';
};

const mapFileDataToProcesses = (rows) => {
  const stamp = Date.now();
  return (Array.isArray(rows) ? rows : []).map((row, index) => ({
    id: `temp-upload-${stamp}-${index}`,
    soxCycle: toStringValue(getFirstValue(row, ['sox_cycle', 'soxCycle', 'SOX Cycle', 'cycle_id', 'cycle'])).toUpperCase(),
    name: inferProcessName(row),
    description: toStringValue(
      getFirstValue(row, ['description', 'Description', 'process_description', 'Process Description'])
    ),
    owner: toStringValue(getFirstValue(row, ['owner', 'Owner', 'process_owner', 'Process Owner'])),
    actors: toStringArray(getFirstValue(row, ['actors', 'actor_list'])),
    systems: toStringArray(getFirstValue(row, ['systems', 'system_list'])),
    source: PROCESS_SOURCE.BATCH_IMPORT
  }));
};

const mapProcessValidationErrorsByRow = mapProcessValidationErrorsByFileDataIndex;

const TAB_MANUAL = 'manual';
const TAB_UPLOAD = 'upload';
const TAB_AI = 'ai';

/** Default page size for AI / bulk draft review tables (overridable via `initialPageSize` on the table). */
const DRAFT_TABLE_PAGE_SIZE = 10;
const DRAFT_TABLE_PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

const PROCESS_DRAFT_VALIDITY_FILTER_OPTIONS = [
  { value: 'all', label: 'All rows' },
  { value: 'invalid', label: 'Invalid rows' },
  { value: 'valid', label: 'Valid rows' }
];

const toArray = (value) =>
  String(value ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

function ProcessCreatePage() {
  const navigate = useNavigate();
  const { cycleId, processId: routeProcessId } = useParams();
  const location = useLocation();
  const isEditMode = Boolean(routeProcessId);
  const queryClient = useQueryClient();
  const createProcessMutation = useCreateProcessMutation();
  const { selectedAuditYearId, selectedAuditYear } = useAuditYear();
  const isLocked = Boolean(selectedAuditYear?.is_locked);

  const {
    data: fetchedEditProcess,
    isLoading: isEditProcessLoading,
    isError: isEditProcessError
  } = useQuery({
    queryKey: ['process', routeProcessId],
    enabled: isEditMode,
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async () => {
      const response = await axiosInstance.get(API_ENDPOINTS.getProcessByIdWithCycle(routeProcessId));
      return response.data?.data ?? response.data?.process ?? response.data ?? null;
    },
  });

  const editProcess = fetchedEditProcess ?? null;

  const processesListPath = useMemo(
    () => soxProcessesListPath(String(cycleId || '')),
    [cycleId]
  );
  const { goBack: navigateBackFromProcessPage } = useProcessBackNavigation(processesListPath);

  const initialTab = location.state?.tab ?? TAB_AI;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [aiDraftProcesses, setAiDraftProcesses] = useState([]);
  const [aiEditedProcesses, setAiEditedProcesses] = useState({});
  const [aiSelectedIds, setAiSelectedIds] = useState(new Set());
  const [aiSourceDocuments, setAiSourceDocuments] = useState([]);
  const [aiAssistantStep, setAiAssistantStep] = useState('library');
  const [aiLibraryDocuments, setAiLibraryDocuments] = useState([]);
  const [manualDocuments, setManualDocuments] = useState([]);
  const [manualDocumentsUploading, setManualDocumentsUploading] = useState(false);
  const manualDocumentsPanelRef = useRef(null);
  // const [aiLibraryActiveTagDocumentId, setAiLibraryActiveTagDocumentId] = useState(null);
  const { startEditing, stopEditing, clearEditing, isFieldEditing } =
    useProcessDraftFieldEditing();

  const [bulkOverlay, setBulkOverlay] = useState({
    open: false,
    statusMessage: '',
    detailMessage: null,
    isComplete: false
  });
  const [uploadValidating, setUploadValidating] = useState(false);
  const [uploadDraftProcesses, setUploadDraftProcesses] = useState([]);
  const [uploadEditedProcesses, setUploadEditedProcesses] = useState({});
  const [uploadSelectedIds, setUploadSelectedIds] = useState(new Set());
  const {
    startEditing: startUploadEditing,
    stopEditing: stopUploadEditing,
    clearEditing: clearUploadEditing,
    isFieldEditing: isUploadFieldEditing
  } = useProcessDraftFieldEditing();
  const [uploadHeaderErrorMessage, setUploadHeaderErrorMessage] = useState(null);
  const [uploadHeaderValid, setUploadHeaderValid] = useState(true);
  /** Uploaded spreadsheet `files.id` for bulk upload finalize (optional `file_id` on POST …/processes). */
  const [uploadBulkFileId, setUploadBulkFileId] = useState(null);
  const [uploadDraftSearchQuery, setUploadDraftSearchQuery] = useState('');
  const [uploadDraftValidityFilter, setUploadDraftValidityFilter] = useState('all');

  const { data: cycleProcessesData } = useQuery({
    queryKey: cycleProcessesQueryKey(cycleId),
    enabled: Boolean(cycleId) && !isEditMode,
    queryFn: async () => {
      const response = await axiosInstance.get(API_ENDPOINTS.getProcesses(cycleId));
      return Array.isArray(response?.data?.processes) ? response.data.processes : [];
    }
  });

  const cycleName = useMemo(() => {
    if (isEditMode) {
      return (
        editProcess?.sox_cycle_name ||
        editProcess?.cycle_name ||
        String(editProcess?.cycle_id || cycleId || '').toUpperCase()
      );
    }
    const first = Array.isArray(cycleProcessesData) && cycleProcessesData.length > 0 ? cycleProcessesData[0] : null;
    return first?.cycle_name || String(cycleId || '').toUpperCase();
  }, [isEditMode, editProcess, cycleProcessesData, cycleId]);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(createProcessSchema),
    defaultValues: {
      name: '',
      description: '',
      owner: '',
      actors: '',
      systems: ''
    }
  });

  const isSavePending = createProcessMutation.isPending || manualDocumentsUploading;

  const handleCreateSubmit = async (values) => {
    let documentsForCreate = manualDocuments;
    if (manualDocumentsPanelRef.current?.uploadPendingDocuments) {
      try {
        documentsForCreate = await manualDocumentsPanelRef.current.uploadPendingDocuments();
        setManualDocuments(documentsForCreate);
      } catch {
        // Panel already surfaces upload errors via toast.
        return;
      }
    }

    const fileIds = extractProcessFileIds(documentsForCreate);
    const payload = {
      name: String(values.name || '').trim(),
      description: String(values.description || '').trim(),
      owner: String(values.owner || '').trim(),
      actors: toArray(values.actors),
      systems: toArray(values.systems),
      source: PROCESS_SOURCE.MANUAL,
      ...(fileIds.length > 0 ? { file_ids: fileIds } : {}),
      ...(selectedAuditYearId ? { audit_year_id: selectedAuditYearId } : {})
    };
    createProcessMutation.mutate(
      { cycleId, payload },
      { onSuccess: () => navigate(processesListPath) }
    );
  };

  const saveAiDraftMutation = useMutation({
    mutationFn: async () => {
      const visible = aiDraftProcesses
        .filter((p) => aiSelectedIds.has(p.id))
        .map((p) => ({ ...p, ...(aiEditedProcesses[p.id] || {}) }));
      if (visible.length === 0) {
        throw new Error('Please select at least one process');
      }
      const selectedLibraryFileIds =
        aiLibraryDocuments.length > 0
          ? aiLibraryDocuments
              .filter((doc) => doc.selected && doc.file_id)
              .map((doc) => doc.file_id)
          : (Array.isArray(aiSourceDocuments) ? aiSourceDocuments : [])
              .map((doc) => doc?.file_id)
              .filter(Boolean);
      if (selectedLibraryFileIds.length === 0) {
        throw new Error('No source documents available');
      }
      const payload = visible.map((p) => ({
        name: String(p.name || '').trim(),
        description: String(p.description || '').trim(),
        owner: String(p.owner || '').trim(),
        status: String(p.status || 'open').trim(),
        source: PROCESS_SOURCE.AI_ASSISTED,
        file_ids:
          Array.isArray(p.file_ids) && p.file_ids.length > 0 ? p.file_ids : selectedLibraryFileIds,
        actors: Array.isArray(p.actors) ? p.actors : toArray(p.actors),
        systems: Array.isArray(p.systems) ? p.systems : toArray(p.systems),
        controls: Array.isArray(p.controls) ? p.controls : [],
        ...(Number.isInteger(Number(p.sequence)) && Number(p.sequence) >= 1
          ? { sequence: Number(p.sequence) }
          : {})
      }));
      const body = { processes: payload };
      if (selectedAuditYearId) body.audit_year_id = selectedAuditYearId;
      return axiosInstance.post(API_ENDPOINTS.finalizeAiAssistedProcesses(cycleId), body);
    },
    onSuccess: async () => {
      toast.success('AI extracted processes saved');
      clearProcessDraftCache(queryClient, cycleId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: cycleProcessesQueryKey(cycleId) }),
        queryClient.invalidateQueries({ queryKey: processesQueryKey(cycleId) }),
        queryClient.invalidateQueries({ queryKey: SOX_CYCLES_QUERY_KEY })
      ]);
      navigate(processesListPath);
    },
    onError: (error) => {
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Failed to save extracted processes')));
    }
  });

  const saveUploadDraftMutation = useMutation({
    mutationFn: async () => {
      const visible = uploadDraftProcesses
        .filter((p) => uploadSelectedIds.has(p.id))
        .map((p) => ({ ...p, ...(uploadEditedProcesses[p.id] || {}) }));
      if (visible.length === 0) {
        throw new Error('Please select at least one process');
      }
      const payload = visible.map((p) => ({
        name: String(p.name || '').trim(),
        description: String(p.description || '').trim(),
        owner: String(p.owner || '').trim(),
        actors: Array.isArray(p.actors) ? p.actors : toArray(p.actors),
        systems: Array.isArray(p.systems) ? p.systems : toArray(p.systems),
        source: PROCESS_SOURCE.BATCH_IMPORT
      }));
      const body = { processes: payload };
      if (uploadBulkFileId) {
        body.file_id = uploadBulkFileId;
      }
      if (selectedAuditYearId) body.audit_year_id = selectedAuditYearId;
      return axiosInstance.post(API_ENDPOINTS.finalizeProcesses(cycleId), body);
    },
    onSuccess: async () => {
      toast.success('Uploaded processes saved');
      setUploadBulkFileId(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['cycle-processes', cycleId] }),
        queryClient.invalidateQueries({ queryKey: ['processes', cycleId] }),
        queryClient.invalidateQueries({ queryKey: ['sox-cycles'] })
      ]);
      navigate(processesListPath);
    },
    onError: (error) => {
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Failed to save uploaded processes')));
    }
  });

  const tabClass = (tab) =>
    `px-4 py-2 text-sm font-medium rounded-md transition-colors ${
      activeTab === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
    }`;

  const getAiFieldValue = useCallback((process, field) => {
    const edited = aiEditedProcesses[process.id];
    if (edited && edited[field] !== undefined) return edited[field];
    return process[field];
  }, [aiEditedProcesses]);

  const updateAiField = useCallback((processId, field, value) => {
    setAiEditedProcesses((prev) => ({
      ...prev,
      [processId]: {
        ...(prev[processId] || {}),
        [field]: value
      }
    }));
  }, []);

  const updateAiSourceFileIds = useCallback((processId, fileIds) => {
    setAiDraftProcesses((prev) =>
      prev.map((process) => (process.id === processId ? { ...process, file_ids: fileIds } : process))
    );
    setAiEditedProcesses((prev) => ({
      ...prev,
      [processId]: {
        ...(prev[processId] || {}),
        file_ids: fileIds
      }
    }));
  }, []);

  const getUploadFieldValue = useCallback((process, field) => {
    const edited = uploadEditedProcesses[process.id];
    if (edited && edited[field] !== undefined) return edited[field];
    return process[field];
  }, [uploadEditedProcesses]);

  const updateUploadField = useCallback((processId, field, value) => {
    setUploadEditedProcesses((prev) => ({
      ...prev,
      [processId]: {
        ...(prev[processId] || {}),
        [field]: value
      }
    }));
  }, []);

  const uploadRowValidationOpts = useMemo(
    () => ({
      showBackendErrors: true,
      applyFrontendValidation: true,
      expectedSoxCycle: cycleId
    }),
    [cycleId]
  );

  const aiRowValidationOpts = useMemo(
    () => ({
      showBackendErrors: false,
      applyFrontendValidation: true,
      includeSoxCycle: false
    }),
    []
  );

  const resetUploadDraftTableFilters = useCallback(() => {
    setUploadDraftSearchQuery('');
    setUploadDraftValidityFilter('all');
  }, []);

  const resetUploadDraftSession = useCallback(
    ({ silent = false } = {}) => {
      setUploadDraftProcesses([]);
      setUploadEditedProcesses({});
      setUploadSelectedIds(new Set());
      setUploadHeaderValid(true);
      setUploadHeaderErrorMessage(null);
      setUploadBulkFileId(null);
      resetUploadDraftTableFilters();
      setBulkOverlay((prev) => ({ ...prev, open: false, isComplete: false }));
      if (!silent) {
        toast.success('Review cleared. Upload a new spreadsheet when you are ready.');
      }
    },
    [resetUploadDraftTableFilters]
  );

  const resetAiExtractionSession = useCallback(() => {
    clearProcessDraftCache(queryClient, cycleId);
    setAiDraftProcesses([]);
    setAiEditedProcesses({});
    setAiSelectedIds(new Set());
    setAiSourceDocuments([]);
    setAiAssistantStep('library');
  }, [cycleId, queryClient]);

  const handleBackToAiLibrary = useCallback(() => {
    resetAiExtractionSession();
  }, [resetAiExtractionSession]);

  const handleLeaveCreatePage = useCallback(() => {
    if (!isEditMode) {
      resetAiExtractionSession();
      resetUploadDraftSession({ silent: true });
      void queryClient.invalidateQueries({ queryKey: ['cycle-processes', cycleId] });
    }
    navigateBackFromProcessPage();
  }, [
    cycleId,
    isEditMode,
    queryClient,
    resetAiExtractionSession,
    resetUploadDraftSession,
    navigateBackFromProcessPage
  ]);

  useEffect(() => {
    if (isEditMode) return undefined;
    return () => {
      clearProcessDraftCache(queryClient, cycleId);
    };
  }, [cycleId, isEditMode, queryClient]);

  const handleAiExtractedProcesses = useCallback((processes, metadata = {}) => {
    const rows = mapAiExtractedToDraftProcesses(processes);
    const sourceDocuments = Array.isArray(metadata?.documents) ? metadata.documents : [];
    setAiSourceDocuments(sourceDocuments);
    setAiDraftProcesses(rows);
    setAiEditedProcesses({});
    setAiSelectedIds(
      defaultSelectedProcessDraftIds(rows, (row, field) => row[field], {
        showBackendErrors: false,
        applyFrontendValidation: true,
        includeSoxCycle: false
      })
    );
    clearEditing();
    setAiAssistantStep('review');
  }, [clearEditing]);

  const displayedUploadDraftProcesses = useMemo(() => {
    let rows = uploadDraftProcesses;
    const q = uploadDraftSearchQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter((p) => {
        const sox = formatSoxCycleDisplay(getUploadFieldValue(p, 'soxCycle')).toLowerCase();
        const name = String(getUploadFieldValue(p, 'name') ?? '').toLowerCase();
        const desc = String(getUploadFieldValue(p, 'description') ?? '').toLowerCase();
        const owner = String(getUploadFieldValue(p, 'owner') ?? '').toLowerCase();
        return sox.includes(q) || name.includes(q) || desc.includes(q) || owner.includes(q);
      });
    }
    if (uploadDraftValidityFilter === 'invalid') {
      rows = rows.filter((p) => draftRowHasAnyMergedError(p, getUploadFieldValue, uploadRowValidationOpts));
    } else if (uploadDraftValidityFilter === 'valid') {
      rows = rows.filter((p) => !draftRowHasAnyMergedError(p, getUploadFieldValue, uploadRowValidationOpts));
    }
    return rows;
  }, [
    uploadDraftProcesses,
    uploadDraftSearchQuery,
    uploadDraftValidityFilter,
    getUploadFieldValue,
    uploadRowValidationOpts
  ]);

  const uploadDraftFiltersActive =
    uploadDraftSearchQuery.trim() !== '' || uploadDraftValidityFilter !== 'all';

  useEffect(() => {
    if (uploadDraftProcesses.length === 0) return;
    setUploadSelectedIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const id of Array.from(prev)) {
        const row = uploadDraftProcesses.find((p) => p.id === id);
        if (!row) {
          next.delete(id);
          changed = true;
          continue;
        }
        const mergedRow = { ...row, ...(uploadEditedProcesses[id] || {}) };
        if (draftRowHasAnyMergedError(mergedRow, getUploadFieldValue, uploadRowValidationOpts)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [uploadDraftProcesses, uploadEditedProcesses, uploadRowValidationOpts, getUploadFieldValue]);

  const getMergedAiFieldValue = useCallback(
    (row, field) => {
      const edited = aiEditedProcesses[row.id];
      if (edited && edited[field] !== undefined) return edited[field];
      return row[field];
    },
    [aiEditedProcesses]
  );

  useEffect(() => {
    if (aiDraftProcesses.length === 0) return;
    setAiSelectedIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const id of Array.from(prev)) {
        const row = aiDraftProcesses.find((p) => p.id === id);
        if (!row) {
          next.delete(id);
          changed = true;
          continue;
        }
        const mergedRow = { ...row, ...(aiEditedProcesses[id] || {}) };
        if (draftRowHasAnyMergedError(mergedRow, getMergedAiFieldValue, aiRowValidationOpts)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [aiDraftProcesses, aiEditedProcesses, aiRowValidationOpts, getMergedAiFieldValue]);

  const ADD_PROCESSES_VALIDATION_TOOLTIP =
    'Before adding the processes, please fix the errors.';

  const uploadSelectedHasValidationErrors = useMemo(
    () =>
      selectedDraftRowsHaveMergedErrors(uploadDraftProcesses, uploadSelectedIds, getUploadFieldValue, {
        showBackendErrors: true,
        applyFrontendValidation: true,
        expectedSoxCycle: cycleId
      }),
    [uploadDraftProcesses, uploadSelectedIds, cycleId, getUploadFieldValue]
  );

  const aiSelectedHasValidationErrors = useMemo(
    () =>
      selectedDraftRowsHaveMergedErrors(aiDraftProcesses, aiSelectedIds, getAiFieldValue, {
        showBackendErrors: false,
        applyFrontendValidation: true,
        expectedSoxCycle: cycleId
      }),
    [aiDraftProcesses, aiSelectedIds, cycleId, getAiFieldValue]
  );

  const isUploadAddDisabled =
    saveUploadDraftMutation.isPending ||
    uploadSelectedIds.size === 0 ||
    uploadSelectedHasValidationErrors ||
    !uploadHeaderValid;

  const showUploadAddValidationTooltip =
    uploadSelectedIds.size > 0 && uploadSelectedHasValidationErrors;

  const handleUploadToggleAll = useCallback(
    (checked) => {
      const displayedIds = new Set(displayedUploadDraftProcesses.map((row) => row.id));
      setUploadSelectedIds((prev) => {
        if (checked) {
          const validIds = defaultSelectedProcessDraftIds(
            displayedUploadDraftProcesses,
            getUploadFieldValue,
            uploadRowValidationOpts
          );
          return new Set([...prev, ...validIds]);
        }
        return new Set([...prev].filter((id) => !displayedIds.has(id)));
      });
    },
    [displayedUploadDraftProcesses, getUploadFieldValue, uploadRowValidationOpts]
  );

  const uploadAddButtonTitle = useMemo(() => {
    if (uploadSelectedIds.size === 0) return 'Select at least one valid process row to add.';
    if (!uploadHeaderValid) return 'File did not pass header validation.';
    return undefined;
  }, [uploadSelectedIds.size, uploadHeaderValid]);

  const isAiAddDisabled =
    saveAiDraftMutation.isPending || aiSelectedIds.size === 0 || aiSelectedHasValidationErrors;

  const showAiAddValidationTooltip = aiSelectedIds.size > 0 && aiSelectedHasValidationErrors;

  const handleDownloadProcessSample = async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.getSampleExcelTemplate('process'), {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'process_upload_sample.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      console.error('Error downloading process sample:', error);
      toast.error('Failed to download sample');
    }
  };

  const runProcessBulkUpload = async (acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (!file || !cycleId) return;
    const ext = file.name?.split('.').pop()?.toLowerCase() || '';
    if (!UPLOAD_EXTENSIONS.includes(ext)) {
      toast.error('Use .xlsx or .csv');
      setUploadHeaderErrorMessage(null);
      return;
    }

    setBulkOverlay({
      open: true,
      statusMessage: 'Uploading file…',
      detailMessage: file.name,
      isComplete: false
    });
    setUploadValidating(true);
    setUploadBulkFileId(null);

    try {
      const userHash = localStorage.getItem('userHash') || '';
      const [uploaded] = await uploadFilesAndComplete([file], userHash, MODULE_UPLOAD.SOX_PROCESS);
      const fileId = uploaded?.file_id;
      if (!fileId) {
        throw new Error('Upload did not return file id');
      }

      setBulkOverlay((prev) => ({
        ...prev,
        statusMessage: 'Validating spreadsheet…',
        detailMessage: 'Checking headers and row data for this cycle.'
      }));

      const { data } = await axiosInstance.post('/api/process-upload/validate', {
        file_id: fileId,
        sox_cycle: cycleId
      });

      if (!data?.header_valid) {
        toast.error(PROCESS_BULK_HEADER_VALIDATION_ERROR_MESSAGE);
        setUploadHeaderErrorMessage(PROCESS_BULK_HEADER_VALIDATION_ERROR_MESSAGE);
        setUploadHeaderValid(false);
        setUploadDraftProcesses([]);
        setUploadEditedProcesses({});
        setUploadSelectedIds(new Set());
        clearUploadEditing();
        setUploadBulkFileId(null);
        resetUploadDraftTableFilters();
        setBulkOverlay((prev) => ({ ...prev, open: false }));
        return;
      }

      setUploadHeaderErrorMessage(null);
      setUploadHeaderValid(true);

      setUploadBulkFileId(fileId);

      const errorByIndex = mapProcessValidationErrorsByRow(data?.error_rows);
      const mapped = mapFileDataToProcesses(data?.file_data || []).map((row, index) => ({
        ...row,
        validation_errors: errorByIndex[index] || []
      }));
      setUploadDraftProcesses(mapped);
      setUploadEditedProcesses({});
      setUploadSelectedIds(
        defaultSelectedProcessDraftIds(mapped, (row, field) => row[field], uploadRowValidationOpts)
      );
      clearUploadEditing();
      resetUploadDraftTableFilters();

      setBulkOverlay((prev) => ({
        ...prev,
        statusMessage: 'Ready to review',
        detailMessage: null,
        isComplete: true
      }));

      await new Promise((r) => setTimeout(r, 550));
      setBulkOverlay((prev) => ({ ...prev, open: false, isComplete: false }));

      if (Number(data?.failed_rows || 0) > 0) {
        toast.error('Some rows failed validation. Fix highlighted rows or deselect them before saving.');
      } else {
        toast.success('File validated. Review and select processes to add.');
      }
    } catch (err) {
      console.error('Process upload validate failed:', err);
      toast.error(resolve403ErrorMessage(err, err?.response?.data?.error || err?.message || 'Validate failed'));
      setUploadHeaderErrorMessage(null);
      setUploadDraftProcesses([]);
      setUploadSelectedIds(new Set());
      setUploadBulkFileId(null);
      resetUploadDraftTableFilters();
      setBulkOverlay((prev) => ({ ...prev, open: false, isComplete: false }));
    } finally {
      setUploadValidating(false);
    }
  };
  const manualInputBgClass = 'bg-white';
  const manualInputClass = `h-10 w-full rounded-lg border border-gray-200 ${manualInputBgClass} px-3 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500`;
  const manualTextareaClass = `w-full rounded-lg border border-gray-200 ${manualInputBgClass} px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500`;

  if (isEditMode && isEditProcessLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-gray-900" />
      </div>
    );
  }

  if (isEditMode && (isEditProcessError || !editProcess)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-6">
        <div className="text-center">
          <p className="text-sm text-red-600">Failed to load process details.</p>
          <button
            type="button"
            onClick={navigateBackFromProcessPage}
            className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-8 py-4">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={isEditMode ? navigateBackFromProcessPage : handleLeaveCreatePage}
              className="p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 rounded-full"
            >
              <ChevronLeft size={18} />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {isEditMode ? editProcess?.name || routeProcessId || 'Process' : 'Create Process'}
              </h1>
              <p className="text-sm text-gray-500">Cycle: {cycleName}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={isEditMode ? navigateBackFromProcessPage : handleLeaveCreatePage}
            className="p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 rounded-full"
            aria-label="Close"
            title="Close"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-8 py-8">
        <ReadOnlyBanner />

        {isEditMode ? (
          <ProcessEditForm cycleId={cycleId} processId={routeProcessId} editProcess={editProcess} />
        ) : (
        <div className="mt-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          {!isEditMode && (
            <div className="space-y-2">
              <div className="flex w-fit items-center gap-1 rounded-lg bg-gray-100 p-1">
                <button type="button" className={tabClass(TAB_MANUAL)} onClick={() => setActiveTab(TAB_MANUAL)}>
                  Manual
                </button>
                <button type="button" className={tabClass(TAB_AI)} onClick={() => setActiveTab(TAB_AI)}>
                  AI Assisted
                </button>
                <button
                  type="button"
                  className={tabClass(TAB_UPLOAD)}
                  onClick={() => setActiveTab(TAB_UPLOAD)}
                >
                  Bulk Upload
                </button>
              </div>
            </div>
          )}

          {activeTab === TAB_MANUAL && (
            <form onSubmit={handleSubmit(handleCreateSubmit)} className="mt-6 space-y-5">
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                  <h2 className="text-base font-semibold text-gray-900">Create Process Manually</h2>
                  <p className="mt-0.5 text-sm text-gray-500">Enter process details and create one process.</p>
                </div>
                <div className="space-y-5 p-6">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Process Name <span className="text-red-500">*</span>
                      </label>
                      <input {...register('name')} className={manualInputClass} />
                      {errors.name?.message ? (
                        <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>
                      ) : null}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        rows={4}
                        {...register('description')}
                        className={`resize-none ${manualTextareaClass}`}
                      />
                      {errors.description?.message ? (
                        <p className="mt-1 text-xs text-red-600">{errors.description.message}</p>
                      ) : null}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
                      <input {...register('owner')} className={manualInputClass} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Actors (comma-separated)
                      </label>
                      <input {...register('actors')} className={manualInputClass} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Systems (comma-separated)
                      </label>
                      <input {...register('systems')} className={manualInputClass} />
                    </div>
                  </div>
                </div>
              </div>

              <ProcessGenerateFlowchartPanel
                ref={manualDocumentsPanelRef}
                cycleId={cycleId}
                documents={manualDocuments}
                onDocumentsChange={setManualDocuments}
                deferPersistence
                documentsOnly
                libraryFileCap={PROCESS_MANUAL_CREATE_FILE_CAP}
                onUploadingChange={setManualDocumentsUploading}
              />

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSavePending || isLocked}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSavePending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Create Process
                </button>
              </div>
            </form>
          )}

          {!isEditMode && activeTab === TAB_UPLOAD && (
            <div className="mt-5">
              <BulkExtractionProgressOverlay
                open={bulkOverlay.open}
                title="Processing Bulk Upload"
                subtitle="We are uploading your spreadsheet and validating each row for this SOX cycle."
                statusMessage={bulkOverlay.statusMessage}
                detailMessage={bulkOverlay.detailMessage}
                isComplete={bulkOverlay.isComplete}
              />
              {uploadDraftProcesses.length === 0 ? (
                <BulkDocumentUploadZone
                  accept={PROCESS_BULK_ACCEPT}
                  multiple={false}
                  disabled={uploadValidating}
                  sectionTitle="Bulk upload processes"
                  sectionSubtitle="Upload a spreadsheet (.xlsx or .csv). We validate the file, map rows to processes, and show them below for review before you add them to this cycle."
                  cardTitle="Upload Process Spreadsheet"
                  cardSubtitle="Drop a file here or choose one from your computer. Same flow as control document extraction."
                  formatsFootnote="Supports Excel (.xlsx) and CSV (.csv)."
                  onDownloadSample={handleDownloadProcessSample}
                  onDropAccepted={runProcessBulkUpload}
                  inlineAlertMessage={uploadHeaderErrorMessage}
                />
              ) : null}

              {uploadDraftProcesses.length > 0 ? (
                <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 px-6 py-4">
                    <div className="min-w-0">
                      <h2 className="text-lg font-medium text-gray-900">
                        Processes ({uploadDraftProcesses.length})
                      </h2>
                      <p className="mt-1 text-sm text-gray-600">
                        {uploadSelectedIds.size} of {uploadDraftProcesses.length} selected
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={resetUploadDraftSession}
                      disabled={uploadValidating || saveUploadDraftMutation.isPending}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label="Clear uploaded spreadsheet and return to upload step"
                    >
                      <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                      Upload New
                    </button>
                  </div>
                  <div className="border-b border-gray-200 bg-gray-50/90 px-4 py-3 sm:px-6">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                      <div className="flex min-w-0 flex-1 flex-col items-stretch gap-3 sm:flex-row sm:items-center">
                        <div className="relative min-w-0 flex-1 sm:max-w-md">
                          <Search
                            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                            aria-hidden
                          />
                          <input
                            type="search"
                            placeholder="Search by SOX cycle, name, description, or owner…"
                            value={uploadDraftSearchQuery}
                            onChange={(e) => setUploadDraftSearchQuery(e.target.value)}
                            className={DRAFT_TOOLBAR_SEARCH_CLASS}
                            aria-label="Search extracted processes"
                          />
                        </div>
                        <AppSelect
                          wrapperClassName="w-full shrink-0 sm:w-auto sm:min-w-[11rem]"
                          size="control"
                          value={uploadDraftValidityFilter}
                          onChange={(e) => setUploadDraftValidityFilter(e.target.value)}
                          aria-label="Filter by validation status"
                          options={PROCESS_DRAFT_VALIDITY_FILTER_OPTIONS}
                        />
                      </div>
                      <div className="flex shrink-0 justify-start sm:justify-end">
                        {uploadDraftFiltersActive ? (
                          <button
                            type="button"
                            onClick={resetUploadDraftTableFilters}
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                          >
                            Clear filters
                          </button>
                        ) : null}
                      </div>
                    </div>
                    {displayedUploadDraftProcesses.length !== uploadDraftProcesses.length ? (
                      <p className="mt-2 text-xs text-gray-500">
                        Showing {displayedUploadDraftProcesses.length} of {uploadDraftProcesses.length} rows
                      </p>
                    ) : null}
                  </div>
                  <ProcessDraftReviewTable
                    rows={displayedUploadDraftProcesses}
                    selectedIds={uploadSelectedIds}
                    onToggleAll={handleUploadToggleAll}
                    selectAllAriaLabel="Select all valid processes on this page"
                    onToggleRow={(processId, checked) => {
                      if (checked) {
                        const row = uploadDraftProcesses.find((p) => p.id === processId);
                        if (
                          row &&
                          draftRowHasAnyMergedError(row, getUploadFieldValue, uploadRowValidationOpts)
                        ) {
                          return;
                        }
                      }
                      setUploadSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (checked) next.add(processId);
                        else next.delete(processId);
                        return next;
                      });
                    }}
                    rowSelectAriaLabel={(row) => `Select ${getUploadFieldValue(row, 'name') || 'process'}`}
                    getFieldValue={getUploadFieldValue}
                    isFieldEditing={isUploadFieldEditing}
                    startEditing={startUploadEditing}
                    stopEditing={stopUploadEditing}
                    updateField={updateUploadField}
                    showBackendValidationErrors
                    applyFrontendValidation
                    initialPageSize={DRAFT_TABLE_PAGE_SIZE}
                    pageSizeOptions={DRAFT_TABLE_PAGE_SIZE_OPTIONS}
                    expectedSoxCycle={cycleId}
                  />
                  <div className="flex items-center justify-end border-t border-gray-200 px-6 py-3">
                    <TooltipProvider>
                      {showUploadAddValidationTooltip ? (
                        <Tooltip content={ADD_PROCESSES_VALIDATION_TOOLTIP} side="top">
                          <span className="inline-flex">
                            <button
                              type="button"
                              onClick={() => saveUploadDraftMutation.mutate()}
                              disabled={isUploadAddDisabled || isLocked}
                              className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {saveUploadDraftMutation.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : null}
                              Add Processes
                            </button>
                          </span>
                        </Tooltip>
                      ) : (
                        <button
                          type="button"
                          title={uploadAddButtonTitle}
                          onClick={() => saveUploadDraftMutation.mutate()}
                          disabled={isUploadAddDisabled}
                          className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {saveUploadDraftMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                          Add Processes
                        </button>
                      )}
                    </TooltipProvider>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {!isEditMode && activeTab === TAB_AI && (
            <div className="mt-5">
              {aiAssistantStep === 'library' ? (
                <AISOXProcessDesigner
                  cycleId={cycleId}
                  cycleName={cycleName}
                  embedded
                  documents={aiLibraryDocuments}
                  onDocumentsChange={setAiLibraryDocuments}
                  onExtractedProcesses={handleAiExtractedProcesses}
                  selectionRequiresLinkedFile={false}
                  defaultDocumentSelected
                />
              ) : null}

              {aiAssistantStep === 'review' && aiDraftProcesses.length === 0 ? (
                <div className="mt-5 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                  <div className="flex items-center justify-between gap-4 border-b border-gray-200 bg-gray-50/80 px-6 py-4">
                    <h2 className="text-lg font-semibold text-gray-900">No processes found</h2>
                    <button
                      type="button"
                      onClick={handleBackToAiLibrary}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                      aria-label="Return to cycle document library"
                    >
                      <ChevronLeft className="h-4 w-4" aria-hidden />
                      Back to library
                    </button>
                  </div>
                  <div className="flex flex-col items-center px-6 py-14 text-center">
                    <div className="mb-4 rounded-full bg-amber-50 p-4">
                      <FileSearch className="h-8 w-8 text-amber-600" aria-hidden />
                    </div>
                    <p className="max-w-md text-sm text-gray-600">
                    Couldn't find any processes related to {cycleName} in the uploaded files. Please retry with different files.
                    </p>
                  </div>
                </div>
              ) : null}

              {aiAssistantStep === 'review' && aiDraftProcesses.length > 0 ? (
                <div className="mt-5 rounded-lg border border-gray-200 bg-white shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 px-6 py-4">
                    <div className="min-w-0">
                      <h2 className="text-lg font-medium text-gray-900">
                        Processes ({aiDraftProcesses.length})
                      </h2>
                      <p className="mt-1 text-sm text-gray-600">
                        {aiSelectedIds.size} of {aiDraftProcesses.length} selected
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleBackToAiLibrary}
                      disabled={saveAiDraftMutation.isPending}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label="Return to cycle document library"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
                      Back to library
                    </button>
                  </div>
                  <ProcessDraftReviewTable
                    rows={aiDraftProcesses}
                    selectedIds={aiSelectedIds}
                    showSoxCycleColumn={false}
                    showSourceDocumentsColumn
                    onToggleRow={(processId, checked) => {
                      if (checked) {
                        const row = aiDraftProcesses.find((p) => p.id === processId);
                        if (row && draftRowHasAnyMergedError(row, getAiFieldValue, aiRowValidationOpts)) {
                          return;
                        }
                      }
                      setAiSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (checked) next.add(processId);
                        else next.delete(processId);
                        return next;
                      });
                    }}
                    rowSelectAriaLabel={(row) => `Select ${getAiFieldValue(row, 'name') || 'process'}`}
                    getFieldValue={getAiFieldValue}
                    isFieldEditing={isFieldEditing}
                    startEditing={startEditing}
                    stopEditing={stopEditing}
                    updateField={updateAiField}
                    onUpdateSourceFileIds={updateAiSourceFileIds}
                    showBackendValidationErrors={false}
                    applyFrontendValidation
                    initialPageSize={DRAFT_TABLE_PAGE_SIZE}
                    pageSizeOptions={DRAFT_TABLE_PAGE_SIZE_OPTIONS}
                  />
                  <div className="flex items-center justify-end border-t border-gray-200 px-6 py-3">
                    <TooltipProvider>
                      {showAiAddValidationTooltip ? (
                        <Tooltip content={ADD_PROCESSES_VALIDATION_TOOLTIP} side="top">
                          <span className="inline-flex">
                            <button
                              type="button"
                              onClick={() => saveAiDraftMutation.mutate()}
                              disabled={isAiAddDisabled || isLocked}
                              className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                            >
                              {saveAiDraftMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                              Add Processes
                            </button>
                          </span>
                        </Tooltip>
                      ) : (
                        <button
                          type="button"
                          onClick={() => saveAiDraftMutation.mutate()}
                          disabled={isAiAddDisabled}
                          className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                        >
                          {saveAiDraftMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                          Add Processes
                        </button>
                      )}
                    </TooltipProvider>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

export default ProcessCreatePage;
